import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { SupportedLanguage, supportedLanguages } from '../i18n/translations';
import { LanguageService } from '../services/language.service';

@Injectable({
  providedIn: 'root'
})
export class LanguageRouteGuard implements CanActivate, CanActivateChild {
  constructor(
    private readonly router: Router,
    private readonly language: LanguageService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.handleRoute(route, state);
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.handleRoute(route, state);
  }

  private handleRoute(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    const rawLanguage = this.resolveLanguageParam(route);
    const normalizedLanguage = rawLanguage?.toLowerCase();

    if (normalizedLanguage && this.isSupportedLanguage(normalizedLanguage)) {
      this.language.setLanguage(normalizedLanguage);

      if (rawLanguage !== normalizedLanguage) {
        const suffix = state.url.replace(/^\/[^/]+/, '');
        return this.router.parseUrl(`/${normalizedLanguage}${suffix}`);
      }

      return true;
    }

    const fallbackLanguage = this.language.currentLanguage;
    const currentPath = state.url.startsWith('/') ? state.url : `/${state.url}`;
    const targetPath = currentPath === '/' ? `/${fallbackLanguage}` : `/${fallbackLanguage}${currentPath}`;
    return this.router.parseUrl(targetPath);
  }

  private isSupportedLanguage(code: string): code is SupportedLanguage {
    return supportedLanguages.includes(code as SupportedLanguage);
  }

  private resolveLanguageParam(route: ActivatedRouteSnapshot): string | null {
    for (let index = route.pathFromRoot.length - 1; index >= 0; index -= 1) {
      const candidate = route.pathFromRoot[index].paramMap.get('lang');
      if (candidate) {
        return candidate;
      }
    }
    return null;
  }
}
